import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HbrButton,
  HbrCard,
  HbrCheckbox,
  HbrDropdown,
  HbrIcon,
  HbrInput,
  HbrMenu,
  HbrMenuItem,
  HbrSpinner,
  HbrTab,
  HbrTabGroup,
  HbrTag,
  HbrTooltip,
} from '@harbor/elements-react-standalone';
import {
  checkCircle,
  clock,
  funnelSimple,
  lock,
  magnifyingGlass,
  sort,
  user,
  users as usersIcon,
} from '@harbor/elements/icons';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchAllItems, postItem, deleteItem, putItem } from '../../../support/queries';
import { RootState } from '../../../support/store';
import StbModal from '../../security-toolbox-ui/StbModal';
import { useOrgChart } from '../../../hooks/useOrgChart';
import { OrgChartRow } from '../../../support/orgScope';
import {
  effectiveSendNotification,
  isAssignmentLocked,
  LOCKED_TOOLTIP,
  requiredLabel,
  subtreeUserRows,
  TargetType,
} from '../../../support/journeyMapAssignmentScope';

type JourneyMap = {
  id: number | string;
  name?: string;
  title?: string;
  description?: string;
  estimated_duration_minutes?: number | null;
  item_count?: number | null;
  created?: string;
  last_updated?: string;
  journey_type?: string | number | null;
  type?: string | number | null;
};

type AssignableUser = {
  id: number | string;
  name?: string;
  full_name?: string;
  email?: string;
  title?: string;
};

type AssignableGroup = {
  id: number | string;
  name?: string;
  description?: string;
  // `${groupType}:${id}` - user groups and Azure AD groups have independent id spaces.
  key: string;
  groupType: TargetType;
};

type JourneyMapAssignment = {
  id: number | string;
  journey_map?: number | string;
  user?: number | string | null;
  user_group?: number | string | null;
  due_date?: string | null;
  priority?: number | null;
  status?: string | null;
  assigned_by?: number | string | null;
  source_type?: string | null;
  is_required?: boolean | null;
  send_notification?: boolean | null;
};

type JourneyMapAssignmentTarget = {
  id: number | string;
  journey_map?: number | string;
  target_type?: string | null;
  target_ref_id?: number | string | null;
  due_date?: string | null;
  priority?: number | null;
  status?: string | null;
  assigned_by?: number | string | null;
  is_required?: boolean | null;
  send_notification?: boolean | null;
};

type SortMode =
  | 'title-asc'
  | 'title-desc'
  | 'updated-desc'
  | 'duration-asc'
  | 'steps-desc';

type Props = {
  journeys?: JourneyMap[];
  getJourneyTypeLabel: (journey: JourneyMap) => string;
};

type SelectableRow = {
  id: string;
  selected: boolean;
  originalSelected: boolean;
  existingId?: number | string;
  status?: string | null;
  priority?: number | null;
  dueDate?: string;
  markedForRemoval?: boolean;
  locked?: boolean;
  isRequired?: boolean;
  targetType?: TargetType;
  refId?: string;
};

const PRIORITY_OPTIONS = [
  { label: 'Very High', value: 100 },
  { label: 'High', value: 75 },
  { label: 'Moderate', value: 50 },
  { label: 'Low', value: 25 },
  { label: 'Very Low', value: 0 },
] as const;

const DEFAULT_PRIORITY = 50;

const asList = (data: any) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const normalizeDateInput = (value?: string | null) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const getJourneyTitle = (j: JourneyMap) => j?.name || j?.title || 'Untitled Journey';
const getUserLabel = (u: AssignableUser) => u?.full_name || u?.name || u?.email || `User ${u?.id}`;
const getGroupLabel = (g: AssignableGroup) => g?.name || `Group ${g?.id}`;
const GROUP_TYPE_LABEL: Record<TargetType, string> = {
  user_group: 'User group',
  azure_ad_group: 'Azure AD group',
};
const getPriorityLabel = (value?: number | null) =>
  PRIORITY_OPTIONS.find((opt) => opt.value === value)?.label || 'Moderate';

const sortJourneys = (rows: JourneyMap[], sortMode: SortMode) => {
  const cloned = [...rows];

  cloned.sort((a, b) => {
    const titleA = getJourneyTitle(a).toLowerCase();
    const titleB = getJourneyTitle(b).toLowerCase();

    switch (sortMode) {
      case 'title-asc':
        return titleA.localeCompare(titleB);
      case 'title-desc':
        return titleB.localeCompare(titleA);
      case 'updated-desc': {
        const aTs = Date.parse(String(a?.last_updated || a?.created || ''));
        const bTs = Date.parse(String(b?.last_updated || b?.created || ''));
        return (Number.isNaN(bTs) ? 0 : bTs) - (Number.isNaN(aTs) ? 0 : aTs);
      }
      case 'duration-asc':
        return Number(a?.estimated_duration_minutes || 0) - Number(b?.estimated_duration_minutes || 0);
      case 'steps-desc':
        return Number(b?.item_count || 0) - Number(a?.item_count || 0);
      default:
        return 0;
    }
  });

  return cloned;
};

const getRowStateLabel = (row: SelectableRow) => {
  if (row.markedForRemoval) return 'Will remove';
  if (!row.originalSelected && row.selected) return 'New';
  if (row.originalSelected && row.selected) return 'Assigned';
  return null;
};

const JourneyMapAssignmentsManagerPage: React.FC<Props> = ({
                                                             journeys = [],
                                                             getJourneyTypeLabel,
                                                           }) => {
  const queryClient = useQueryClient();

  const authz = useSelector((state: RootState) => state.user?.authz);
  const isManager = useSelector((state: RootState) => state.user?.userData?.isManager);
  const managerUserId = useSelector((state: RootState) => state.user?.userData?.id);

  const permSet = useMemo(() => new Set<string>(authz?.permissions || []), [authz?.permissions]);
  const isJourneyMapAdmin =
    permSet.has('journey-map-admin.view') || permSet.has('journey-map.assign');

  const canAssignGroups = isJourneyMapAdmin;
  const canAssignUsers = !!isManager || isJourneyMapAdmin;

  const [searchJourney, setSearchJourney] = useState('');
  const [searchTarget, setSearchTarget] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('updated-desc');
  const [selectedJourneyType, setSelectedJourneyType] = useState<string>('All');
  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(null);
  const [targetMode, setTargetMode] = useState<'users' | 'groups'>('users');

  const [priority, setPriority] = useState<number>(DEFAULT_PRIORITY);
  const [dueDate, setDueDate] = useState<string>('');
  const [isRequired, setIsRequired] = useState<boolean>(false);
  // null = follow Required (required notifies, optional stays quiet); true/false = explicit choice.
  const [notifyOverride, setNotifyOverride] = useState<boolean | null>(null);
  const [userSelection, setUserSelection] = useState<Record<string, SelectableRow>>({});
  const [groupSelection, setGroupSelection] = useState<Record<string, SelectableRow>>({});

  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultModalTitle, setResultModalTitle] = useState('Assignment Results');
  const [resultModalBody, setResultModalBody] = useState<React.ReactNode>(null);
  const [resultModalRole, setResultModalRole] = useState<'information' | 'save' | 'update' | 'delete'>('information');

  const hydrationKeyRef = useRef<string>('');

  const usersQuery = useQuery({
    queryKey: ['assignable-users', isJourneyMapAdmin],
    queryFn: async () => {
      const data = await fetchAllItems('db/user/', {});
      return asList(data);
    },
    ...{
        enabled: isJourneyMapAdmin,
        staleTime: 60_000,
      },
  });

  // A manager assigns to everyone beneath them in the management chain, not only direct reports.
  const isScopedManager = !isJourneyMapAdmin && !!isManager;
  const orgChartQuery = useOrgChart({ enabled: isScopedManager });
  const subtreeRows = useMemo(
    () => (isScopedManager ? subtreeUserRows((orgChartQuery.data as OrgChartRow[] | undefined) ?? [], managerUserId) : []),
    [isScopedManager, orgChartQuery.data, managerUserId]
  );
  const subtreeIds = useMemo(
    () => new Set<number>(subtreeRows.map((r) => Number(r.user_id))),
    [subtreeRows]
  );

  const groupsQuery = useQuery({
    queryKey: ['assignable-groups', isJourneyMapAdmin],
    queryFn: async () => {
      if (!canAssignGroups) return [];
      const [userGroups, azureGroups] = await Promise.all([
        fetchAllItems('db/user-group/', {}),
        fetchAllItems('db/azure-ad-group/', {}),
      ]);
      const withType = (rows: any[], groupType: TargetType): AssignableGroup[] =>
        rows.map((g) => ({ ...g, groupType, key: `${groupType}:${g.id}` }));
      return [
        ...withType(asList(userGroups), 'user_group'),
        ...withType(asList(azureGroups), 'azure_ad_group'),
      ];
    },
    ...{
        enabled: canAssignGroups,
        staleTime: 60_000,
      },
  });

  const existingAssignmentsQuery = useQuery({
    queryKey: ['journey-map-existing-assignments', selectedJourneyId, canAssignGroups],
    queryFn: async () => {
      if (!selectedJourneyId) {
        return {
          userAssignments: [] as JourneyMapAssignment[],
          groupTargets: [] as JourneyMapAssignmentTarget[],
        };
      }

      const userAssignmentsRaw = await fetchAllItems('db/journey-map-assignment/', {
        journey_map: Number(selectedJourneyId),
      });

      const groupTargetsRaw = canAssignGroups
        ? await fetchAllItems('db/journey-map-assignment-target/', {
          journey_map: Number(selectedJourneyId),
        })
        : [];

      return {
        userAssignments: asList(userAssignmentsRaw),
        groupTargets: asList(groupTargetsRaw),
      };
    },
    ...{
        enabled: !!selectedJourneyId,
        staleTime: 15_000,
      },
  });

  const users = useMemo<AssignableUser[]>(() => {
    if (isScopedManager) {
      return subtreeRows
        .map((r) => ({
          id: r.user_id,
          email: r.user_email,
          title: r.is_manager ? 'Manager' : undefined,
        }))
        .sort((a, b) => String(a.email).localeCompare(String(b.email)));
    }
    return Array.isArray(usersQuery.data) ? usersQuery.data : [];
  }, [isScopedManager, subtreeRows, usersQuery.data]);

  const groups = useMemo<AssignableGroup[]>(
    () => (Array.isArray(groupsQuery.data) ? groupsQuery.data : []),
    [groupsQuery.data]
  );

  useEffect(() => {
    if (!selectedJourneyId) {
      hydrationKeyRef.current = '';
      setUserSelection({});
      setGroupSelection({});
      setPriority(DEFAULT_PRIORITY);
      setDueDate('');
      setIsRequired(false);
      setNotifyOverride(null);
      return;
    }

    const userAssignments = asList(existingAssignmentsQuery.data?.userAssignments);
    const groupTargets = asList(existingAssignmentsQuery.data?.groupTargets);

    const hydrationKey = JSON.stringify({
      selectedJourneyId,
      userAssignments: userAssignments.map((x: JourneyMapAssignment) => ({
        id: x.id,
        user: x.user,
        priority: x.priority,
        due_date: x.due_date,
        status: x.status,
        by: x.assigned_by,
        req: x.is_required,
        notify: x.send_notification,
      })),
      groupTargets: groupTargets.map((x: JourneyMapAssignmentTarget) => ({
        id: x.id,
        type: x.target_type,
        ref: x.target_ref_id,
        priority: x.priority,
        due_date: x.due_date,
        status: x.status,
        req: x.is_required,
        notify: x.send_notification,
      })),
      userIds: users.map((u) => u.id),
      groupKeys: groups.map((g) => g.key),
      subtree: Array.from(subtreeIds).sort(),
    });

    if (hydrationKeyRef.current === hydrationKey) return;
    hydrationKeyRef.current = hydrationKey;

    const nextUserSelection: Record<string, SelectableRow> = {};
    const nextGroupSelection: Record<string, SelectableRow> = {};

    const assignmentPriorities: number[] = [];
    const assignmentDueDates: string[] = [];
    const assignmentRequired: boolean[] = [];
    const assignmentNotify: Array<boolean | null> = [];

    const userAssignmentsByUserId: Record<string, JourneyMapAssignment> = {};
    userAssignments.forEach((assignment: JourneyMapAssignment) => {
      if (assignment?.user == null) return;
      const key = String(assignment.user);
      if (!userAssignmentsByUserId[key]) {
        userAssignmentsByUserId[key] = assignment;
        assignmentRequired.push(!!assignment.is_required);
        assignmentNotify.push(assignment.send_notification ?? null);
        if (typeof assignment.priority === 'number') assignmentPriorities.push(assignment.priority);
        const normalized = normalizeDateInput(assignment.due_date);
        if (normalized) assignmentDueDates.push(normalized);
      }
    });

    const groupTargetsByGroupId: Record<string, JourneyMapAssignmentTarget> = {};
    groupTargets.forEach((target: JourneyMapAssignmentTarget) => {
      if (
        (target?.target_type !== 'user_group' && target?.target_type !== 'azure_ad_group') ||
        target?.target_ref_id == null
      ) {
        return;
      }
      const key = `${target.target_type}:${target.target_ref_id}`;
      if (!groupTargetsByGroupId[key]) {
        groupTargetsByGroupId[key] = target;
        assignmentRequired.push(!!target.is_required);
        assignmentNotify.push(target.send_notification ?? null);
        if (typeof target.priority === 'number') assignmentPriorities.push(target.priority);
        const normalized = normalizeDateInput(target.due_date);
        if (normalized) assignmentDueDates.push(normalized);
      }
    });

    users.forEach((u) => {
      const id = String(u.id);
      const existing = userAssignmentsByUserId[id];
      nextUserSelection[id] = {
        id,
        selected: !!existing,
        originalSelected: !!existing,
        existingId: existing?.id,
        status: existing?.status ?? null,
        priority: typeof existing?.priority === 'number' ? existing.priority : null,
        dueDate: normalizeDateInput(existing?.due_date),
        markedForRemoval: false,
        isRequired: !!existing?.is_required,
        locked:
          !!existing &&
          isAssignmentLocked({
            isAdmin: isJourneyMapAdmin,
            actorId: managerUserId,
            assignedBy: existing.assigned_by,
            sourceType: existing.source_type,
            subtreeIds,
          }),
      };
    });

    groups.forEach((g) => {
      const id = g.key;
      const existing = groupTargetsByGroupId[id];
      nextGroupSelection[id] = {
        id,
        selected: !!existing,
        originalSelected: !!existing,
        existingId: existing?.id,
        status: existing?.status ?? null,
        priority: typeof existing?.priority === 'number' ? existing.priority : null,
        dueDate: normalizeDateInput(existing?.due_date),
        markedForRemoval: false,
        isRequired: !!existing?.is_required,
        targetType: g.groupType,
        refId: String(g.id),
        locked: false,
      };
    });

    const distinctPriorities = Array.from(new Set(assignmentPriorities));
    const distinctDueDates = Array.from(new Set(assignmentDueDates));

    setUserSelection(nextUserSelection);
    setGroupSelection(nextGroupSelection);
    setPriority(distinctPriorities.length === 1 ? distinctPriorities[0] : DEFAULT_PRIORITY);
    setDueDate(distinctDueDates.length === 1 ? distinctDueDates[0] : '');

    const distinctRequired = Array.from(new Set(assignmentRequired));
    const nextRequired = distinctRequired.length === 1 ? distinctRequired[0] : false;
    setIsRequired(nextRequired);

    // Only carry an explicit notification choice that differs from what Required would imply.
    const distinctNotify = Array.from(new Set(assignmentNotify));
    const onlyNotify = distinctNotify.length === 1 ? distinctNotify[0] : null;
    setNotifyOverride(onlyNotify !== null && onlyNotify !== nextRequired ? onlyNotify : null);
  }, [
    selectedJourneyId,
    existingAssignmentsQuery.data,
    users,
    groups,
    subtreeIds,
    isJourneyMapAdmin,
    managerUserId,
  ]);

  const availableJourneyTypes = useMemo(() => {
    const values = Array.from(
      new Set(journeys.map((j) => getJourneyTypeLabel(j)).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    return ['All', ...values];
  }, [journeys, getJourneyTypeLabel]);

  const filteredJourneys = useMemo(() => {
    const q = searchJourney.trim().toLowerCase();

    const filtered = journeys.filter((j) => {
      const title = getJourneyTitle(j).toLowerCase();
      const description = String(j?.description || '').toLowerCase();
      const typeLabel = getJourneyTypeLabel(j);

      const matchesSearch = !q || title.includes(q) || description.includes(q);
      const matchesType = selectedJourneyType === 'All' || typeLabel === selectedJourneyType;

      return matchesSearch && matchesType;
    });

    return sortJourneys(filtered, sortMode);
  }, [journeys, searchJourney, selectedJourneyType, sortMode, getJourneyTypeLabel]);

  const filteredUsers = useMemo(() => {
    const q = searchTarget.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = getUserLabel(u).toLowerCase();
      const email = String(u?.email || '').toLowerCase();
      const title = String(u?.title || '').toLowerCase();
      return name.includes(q) || email.includes(q) || title.includes(q);
    });
  }, [users, searchTarget]);

  const filteredGroups = useMemo(() => {
    const q = searchTarget.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      const name = getGroupLabel(g).toLowerCase();
      const description = String(g?.description || '').toLowerCase();
      const typeLabel = GROUP_TYPE_LABEL[g.groupType].toLowerCase();
      return name.includes(q) || description.includes(q) || typeLabel.includes(q);
    });
  }, [groups, searchTarget]);

  const selectedJourney =
    journeys.find((j) => String(j.id) === String(selectedJourneyId)) || null;

  const activeSelection = targetMode === 'users' ? userSelection : groupSelection;

  const selectedCount = useMemo(
    () =>
      Object.values(activeSelection).filter(
        (row) => row.selected && !row.markedForRemoval
      ).length,
    [activeSelection]
  );

  const existingCount = useMemo(
    () => Object.values(activeSelection).filter((row) => row.originalSelected).length,
    [activeSelection]
  );

  const newCount = useMemo(
    () =>
      Object.values(activeSelection).filter(
        (row) => row.selected && !row.originalSelected && !row.markedForRemoval
      ).length,
    [activeSelection]
  );

  const willRemoveCount = useMemo(
    () =>
      Object.values(activeSelection).filter(
        (row) => row.originalSelected && row.markedForRemoval
      ).length,
    [activeSelection]
  );

  const toggleUser = (id: string) => {
    setUserSelection((prev) => {
      const row = prev[id];
      if (!row || row.originalSelected) return prev;
      return {
        ...prev,
        [id]: {
          ...row,
          selected: !row.selected,
        },
      };
    });
  };

  const toggleGroup = (id: string) => {
    setGroupSelection((prev) => {
      const row = prev[id];
      if (!row || row.originalSelected) return prev;
      return {
        ...prev,
        [id]: {
          ...row,
          selected: !row.selected,
        },
      };
    });
  };

  const markUserForRemoval = (id: string) => {
    setUserSelection((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        markedForRemoval: true,
        selected: false,
      },
    }));
  };

  const undoRemoveUser = (id: string) => {
    setUserSelection((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        markedForRemoval: false,
        selected: true,
      },
    }));
  };

  const markGroupForRemoval = (id: string) => {
    setGroupSelection((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        markedForRemoval: true,
        selected: false,
      },
    }));
  };

  const undoRemoveGroup = (id: string) => {
    setGroupSelection((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        markedForRemoval: false,
        selected: true,
      },
    }));
  };

  const isSelectable = (row?: SelectableRow) => !!row && !row.originalSelected && !row.locked;

  // Selects every visible, not-yet-assigned target so a manager can assign a whole team at once.
  const selectAllFiltered = () => {
    if (targetMode === 'users') {
      const ids = new Set(filteredUsers.map((u) => String(u.id)));
      setUserSelection((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (isSelectable(next[id])) next[id] = { ...next[id], selected: true };
        });
        return next;
      });
      return;
    }
    const keys = new Set(filteredGroups.map((g) => g.key));
    setGroupSelection((prev) => {
      const next = { ...prev };
      keys.forEach((id) => {
        if (isSelectable(next[id])) next[id] = { ...next[id], selected: true };
      });
      return next;
    });
  };

  const clearNewSelection = () => {
    const clear = (prev: Record<string, SelectableRow>) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (isSelectable(next[id])) next[id] = { ...next[id], selected: false };
      });
      return next;
    };
    if (targetMode === 'users') setUserSelection(clear);
    else setGroupSelection(clear);
  };

  const notifyEffective = effectiveSendNotification(isRequired, notifyOverride);

  const openResultModal = (
    title: string,
    body: React.ReactNode,
    role: 'information' | 'save' | 'update' | 'delete' = 'information'
  ) => {
    setResultModalTitle(title);
    setResultModalBody(body);
    setResultModalRole(role);
    setResultModalOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedJourneyId) {
        return {
          createdResults: [],
          updatedResults: [],
          removedResults: [],
        };
      }

      if (targetMode === 'users') {
        const rows = users
          .map((u) => userSelection[String(u.id)])
          .filter((row) => !!row && !row.locked) as SelectableRow[];

        const toCreate = rows.filter(
          (row) => row.selected && !row.originalSelected && !row.markedForRemoval
        );
        const toUpdate = rows.filter(
          (row) => row.originalSelected && row.selected && !row.markedForRemoval
        );
        const toRemove = rows.filter((row) => row.originalSelected && row.markedForRemoval);

        const createdResults = await Promise.allSettled(
          toCreate.map((row) =>
            postItem('db/journey-map-assignment', {
              journey_map: Number(selectedJourneyId),
              user: Number(row.id),
              source_type: isJourneyMapAdmin ? 'manual' : 'manager',
              priority,
              due_date: dueDate || null,
              is_required: isRequired,
              send_notification: notifyOverride,
            })
          )
        );

        const updatedResults = await Promise.allSettled(
          toUpdate.map((row) => {
            if (!row.existingId) {
              return Promise.reject(new Error(`Missing assignment id for user ${row.id}`));
            }
            return putItem('db/journey-map-assignment', {
              id: row.existingId,
              journey_map: Number(selectedJourneyId),
              user: Number(row.id),
              source_type: isJourneyMapAdmin ? 'manual' : 'manager',
              priority,
              due_date: dueDate || null,
              is_required: isRequired,
              send_notification: notifyOverride,
            });
          })
        );

        const removedResults = await Promise.allSettled(
          toRemove.map((row) => {
            if (!row.existingId) {
              return Promise.reject(new Error(`Missing assignment id for user ${row.id}`));
            }
            return deleteItem(`db/journey-map-assignment/${row.existingId}/`);
          })
        );

        return { createdResults, updatedResults, removedResults };
      }

      const rows = groups.map((g) => groupSelection[g.key]).filter(Boolean) as SelectableRow[];

      const toCreate = rows.filter(
        (row) => row.selected && !row.originalSelected && !row.markedForRemoval
      );
      const toUpdate = rows.filter(
        (row) => row.originalSelected && row.selected && !row.markedForRemoval
      );
      const toRemove = rows.filter((row) => row.originalSelected && row.markedForRemoval);

      const createdResults = await Promise.allSettled(
        toCreate.map((row) =>
          postItem('db/journey-map-assignment-target/', {
            journey_map: Number(selectedJourneyId),
            target_type: row.targetType,
            target_ref_id: Number(row.refId),
            source_type: isJourneyMapAdmin ? 'manual' : 'manager',
            priority,
            due_date: dueDate || null,
            is_required: isRequired,
            send_notification: notifyOverride,
          })
        )
      );

      const updatedResults = await Promise.allSettled(
        toUpdate.map((row) => {
          if (!row.existingId) {
            return Promise.reject(new Error(`Missing assignment target id for group ${row.id}`));
          }
          return putItem('db/journey-map-assignment-target', {
            id: row.existingId,
            journey_map: Number(selectedJourneyId),
            target_type: row.targetType,
            target_ref_id: Number(row.refId),
            source_type: isJourneyMapAdmin ? 'manual' : 'manager',
            priority,
            due_date: dueDate || null,
            is_required: isRequired,
            send_notification: notifyOverride,
          });
        })
      );

      const removedResults = await Promise.allSettled(
        toRemove.map((row) => {
          if (!row.existingId) {
            return Promise.reject(new Error(`Missing assignment target id for group ${row.id}`));
          }
          return deleteItem(`db/journey-map-assignment-target/${row.existingId}/`);
        })
      );

      return { createdResults, updatedResults, removedResults };
    },
    onSuccess: async (results) => {
      const createdResults = Array.isArray(results?.createdResults) ? results.createdResults : [];
      const updatedResults = Array.isArray(results?.updatedResults) ? results.updatedResults : [];
      const removedResults = Array.isArray(results?.removedResults) ? results.removedResults : [];

      const createdCount = createdResults.filter((r) => r.status === 'fulfilled').length;
      const updatedCount = updatedResults.filter((r) => r.status === 'fulfilled').length;
      const removedCount = removedResults.filter((r) => r.status === 'fulfilled').length;

      const rejected = [
        ...createdResults.filter((r) => r.status === 'rejected'),
        ...updatedResults.filter((r) => r.status === 'rejected'),
        ...removedResults.filter((r) => r.status === 'rejected'),
      ] as PromiseRejectedResult[];

      const errors = rejected.map((r) => {
        const reason: any = r.reason;
        return reason?.message || 'Assignment save failed.';
      });

      openResultModal(
        rejected.length ? 'Assignment changes completed with errors' : 'Assignment changes saved',
        <div className="hbr-css__layout-col-3xs">
          <div className="hbr-css__text-primary-sm-semibold">
            {createdCount} created, {updatedCount} updated, {removedCount} removed
          </div>
          {errors.length ? (
            <div className="hbr-css__layout-col-3xs">
              {errors.slice(0, 10).map((err, idx) => (
                <div key={idx} className="hbr-css__text-secondary-xs">
                  • {err}
                </div>
              ))}
            </div>
          ) : (
            <div className="hbr-css__text-secondary-xs">All requested changes were saved.</div>
          )}
        </div>,
        rejected.length ? 'information' : 'save'
      );
      hydrationKeyRef.current = '';
      await queryClient.invalidateQueries({ queryKey: ['journey-map-assignments'] });
      await queryClient.invalidateQueries({ queryKey: ['journey-map-assignment-targets'] });
      await queryClient.invalidateQueries({
        queryKey: ['journey-map-existing-assignments', selectedJourneyId, canAssignGroups],
      });
    },
    onError: (error: any) => {
      openResultModal(
        'Assignment save failed',
        <div className="hbr-css__layout-col-3xs">
          <div className="hbr-css__text-primary-sm-semibold">Unable to save assignment changes.</div>
          <div className="hbr-css__text-secondary-xs">
            {error?.message || 'An unexpected error occurred.'}
          </div>
        </div>,
        'information'
      );
    },
  });

  const loading =
    (isJourneyMapAdmin && usersQuery.isLoading) ||
    (isScopedManager && orgChartQuery.isLoading) ||
    (canAssignGroups && groupsQuery.isLoading) ||
    (!!selectedJourneyId && existingAssignmentsQuery.isLoading);

  if (!canAssignUsers && !canAssignGroups) {
    return (
      <>
        <HbrCard
          style={
            {
              '--box-shadow': 'none',
              '--border-width': '2px',
              '--border-color': 'var(--hbr-color-neutral-border-weak)',
            } as React.CSSProperties
          }
        >
          <div className="hbr-css__layout-col-3xs">
            <div className="hbr-css__text-primary-sm-semibold">Assignments unavailable</div>
            <div className="hbr-css__text-secondary-sm">
              You do not currently have any assignment scope.
            </div>
          </div>
        </HbrCard>

        <StbModal
          open={resultModalOpen}
          role={resultModalRole}
          title={resultModalTitle}
          onClose={async () => setResultModalOpen(false)}
          onSubmit={async () => setResultModalOpen(false)}
          submitLabel="Close"
          hideCancel
        >
          {resultModalBody}
        </StbModal>
      </>
    );
  }

  return (
    <>
      <div className="hbr-css__layout-col-lg">
        <HbrCard
          style={
            {
              '--box-shadow': 'none',
              '--border-width': '2px',
              '--border-color': 'var(--hbr-color-neutral-border-weak)',
              '--border-radius': '10px',
            } as React.CSSProperties
          }
        >
          <div
            className="hbr-css__layout-row-md hbr-css__layout-justify-between hbr-css__layout-align-end"
            style={{ gap: '16px', flexWrap: 'wrap' }}
          >
            <div className="hbr-css__layout-col-sm" style={{ flex: '1 1 420px', minWidth: 0 }}>
              <div className="hbr-css__text-heading-md-bold">
                {isJourneyMapAdmin ? 'Assign Journeys' : 'Assign to Your Team'}
              </div>
              <div className="hbr-css__text-secondary-sm">
                Search, sort, and filter journeys before assigning them to eligible targets.
              </div>
            </div>

            <div
              className="hbr-css__layout-row-sm hbr-css__layout-wrap"
              style={{ alignItems: 'center', flex: '1 1 520px', justifyContent: 'flex-end' }}
            >
              <div style={{ minWidth: 220, flex: '1 1 220px', maxWidth: 340 }}>
                <HbrInput
                  value={searchJourney}
                  placeholder="Search journeys"
                  onHbr-input={(e: any) =>
                    setSearchJourney(String(e?.target?.value ?? e?.detail?.value ?? ''))
                  }
                >
                  <HbrIcon slot="prefix" asset={magnifyingGlass} />
                </HbrInput>
              </div>

              <HbrDropdown hoist menu distance={8}>
                <HbrButton slot="trigger" variant="outline">
                  <HbrIcon slot="prefix" asset={funnelSimple} />
                  Type: {selectedJourneyType}
                </HbrButton>
                <HbrMenu>
                  {availableJourneyTypes.map((type) => (
                    <HbrMenuItem key={type} onClick={() => setSelectedJourneyType(type)}>
                      {type}
                    </HbrMenuItem>
                  ))}
                </HbrMenu>
              </HbrDropdown>

              <HbrDropdown hoist menu distance={8}>
                <HbrButton slot="trigger" variant="outline">
                  <HbrIcon slot="prefix" asset={sort} />
                  Sort
                </HbrButton>
                <HbrMenu>
                  <HbrMenuItem onClick={() => setSortMode('updated-desc')}>Recently Updated</HbrMenuItem>
                  <HbrMenuItem onClick={() => setSortMode('title-asc')}>Title A–Z</HbrMenuItem>
                  <HbrMenuItem onClick={() => setSortMode('title-desc')}>Title Z–A</HbrMenuItem>
                  <HbrMenuItem onClick={() => setSortMode('duration-asc')}>Shortest Duration</HbrMenuItem>
                  <HbrMenuItem onClick={() => setSortMode('steps-desc')}>Most Steps</HbrMenuItem>
                </HbrMenu>
              </HbrDropdown>
            </div>
          </div>
        </HbrCard>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(320px, 1fr) minmax(440px, 1.2fr)',
            gap: '16px',
            alignItems: 'start',
          }}
        >
          <HbrCard
            style={
              {
                '--border-width': '2px',
                '--border-color': 'var(--hbr-color-neutral-border-weak)',
              } as React.CSSProperties
            }
          >
            <div className="hbr-css__layout-col-md">
              <div className="hbr-css__layout-col-3xs">
                <div className="hbr-css__text-heading-md-bold">Select Journey</div>
                <div className="hbr-css__text-secondary-sm">
                  Choose a journey map to assign.
                </div>
              </div>

              <div
                className="hbr-css__layout-col-sm"
                style={{
                  maxHeight: '520px',
                  overflowY: 'auto',
                  paddingRight: '4px',
                }}
              >
                {filteredJourneys.map((journey) => {
                  const selected = String(selectedJourneyId) === String(journey.id);

                  return (
                    <HbrCard
                      key={String(journey.id)}
                      selectable
                      selected={selected}
                      role="button"
                      sentiment={selected ? 'info' : undefined}
                      onClick={() => {
                        setSelectedJourneyId(String(journey.id));
                      }}
                      style={
                        {
                          '--box-shadow': 'none',
                          '--border-width': '2px',
                        } as React.CSSProperties
                      }
                    >
                      <div className="hbr-css__layout-col-3xs">
                        <div className="hbr-css__text-primary-sm-semibold">
                          {getJourneyTitle(journey)}
                        </div>
                        <div className="hbr-css__text-secondary-xs">
                          {journey?.description || 'No description available.'}
                        </div>
                        <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
                          <HbrTag size="small">{getJourneyTypeLabel(journey)}</HbrTag>
                          {journey?.item_count != null ? (
                            <HbrTag size="small">{journey.item_count} steps</HbrTag>
                          ) : null}
                          {journey?.estimated_duration_minutes ? (
                            <HbrTag size="small">
                              <span className="hbr-css__layout-row-3xs hbr-css__layout-align-center">
                                <HbrIcon asset={clock} />
                                {journey.estimated_duration_minutes} min
                              </span>
                            </HbrTag>
                          ) : null}
                        </div>
                      </div>
                    </HbrCard>
                  );
                })}
              </div>
            </div>
          </HbrCard>

          <HbrCard
            style={
              {
                '--border-width': '2px',
                '--border-color': 'var(--hbr-color-neutral-border-weak)',
              } as React.CSSProperties
            }
          >
            <div className="hbr-css__layout-col-md">
              <div className="hbr-css__layout-col-3xs">
                <div className="hbr-css__text-heading-md-bold">
                  {isJourneyMapAdmin ? 'Assign to Users or Groups' : 'Assign to Your Team'}
                </div>
                <div className="hbr-css__text-secondary-sm">
                  {selectedJourney
                    ? `Review and update assignments for "${getJourneyTitle(selectedJourney)}".`
                    : 'Select a journey first.'}
                </div>
              </div>

              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                    '--border-color': 'var(--hbr-color-neutral-border-weak)',
                    backgroundColor: 'var(--hbr-color-neutral-bg-medium)',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-md">
                  <div className="hbr-css__text-heading-sm-bold">Assignment Settings</div>

                  <div
                    className="hbr-css__layout-row-md hbr-css__layout-wrap"
                    style={{ alignItems: 'end' }}
                  >
                    <div className="hbr-css__layout-col-3xs" style={{ minWidth: 220, flex: '1 1 220px' }}>
                      <div className="hbr-css__text-secondary-xs">Priority</div>
                      <select
                        value={String(priority)}
                        onChange={(e) => setPriority(Number(e.target.value))}
                        style={{
                          width: '100%',
                          minHeight: '40px',
                          borderRadius: '8px',
                          border: '1px solid var(--hbr-color-neutral-border-weak)',
                          background: 'var(--hbr-color-neutral-bg-weakest)',
                          color: 'var(--hbr-color-text-primary)',
                          padding: '0 12px',
                        }}
                      >
                        {PRIORITY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="hbr-css__layout-col-3xs" style={{ minWidth: 220, flex: '1 1 220px' }}>
                      <div className="hbr-css__text-secondary-xs">Due Date (optional)</div>
                      <HbrInput
                        type="date"
                        value={dueDate}
                        onHbr-input={(e: any) =>
                          setDueDate(String(e?.target?.value ?? e?.detail?.value ?? ''))
                        }
                      />
                    </div>
                  </div>

                  <div
                    className="hbr-css__layout-row-md hbr-css__layout-wrap"
                    style={{ alignItems: 'end' }}
                  >
                    <div className="hbr-css__layout-col-3xs" style={{ minWidth: 220, flex: '1 1 220px' }}>
                      <div className="hbr-css__text-secondary-xs">Assignment type</div>
                      <div className="hbr-css__layout-row-xs" role="group" aria-label="Assignment type">
                        <HbrButton
                          variant={isRequired ? 'outline' : 'fill'}
                          aria-pressed={!isRequired}
                          onClick={() => {
                            setIsRequired(false);
                            setNotifyOverride(null);
                          }}
                        >
                          Optional
                        </HbrButton>
                        <HbrButton
                          variant={isRequired ? 'fill' : 'outline'}
                          aria-pressed={isRequired}
                          onClick={() => {
                            setIsRequired(true);
                            setNotifyOverride(null);
                          }}
                        >
                          Required
                        </HbrButton>
                      </div>
                    </div>

                    <div className="hbr-css__layout-col-3xs" style={{ minWidth: 220, flex: '1 1 220px' }}>
                      <HbrCheckbox
                        checked={notifyEffective}
                        onHbr-change={(e: any) => {
                          const checked = !!e?.target?.checked;
                          setNotifyOverride(checked === isRequired ? null : checked);
                        }}
                      >
                        Send Webex notification
                      </HbrCheckbox>
                      <div className="hbr-css__text-secondary-xs">
                        {isRequired
                          ? 'Required assignments notify by default.'
                          : 'Optional assignments are quiet by default.'}
                      </div>
                    </div>
                  </div>

                  <div className="hbr-css__text-secondary-xs">
                    Selected targets will all receive these values when you save. Each card shows the current assigned values, if any.
                  </div>
                </div>
              </HbrCard>

              {canAssignGroups ? (
                <HbrTabGroup>
                  <HbrTab
                    slot="nav"
                    panel="users"
                    active={targetMode === 'users'}
                    onHbr-click={() => setTargetMode('users')}
                  >
                    Users
                  </HbrTab>
                  <HbrTab
                    slot="nav"
                    panel="groups"
                    active={targetMode === 'groups'}
                    onHbr-click={() => setTargetMode('groups')}
                  >
                    Groups
                  </HbrTab>
                </HbrTabGroup>
              ) : null}

              {loading ? (
                <div style={{ minHeight: 240, display: 'grid', placeItems: 'center' }}>
                  <HbrSpinner size="large">
                    <div slot="label">
                      {selectedJourneyId ? 'Loading existing assignments...' : 'Loading assignable targets...'}
                    </div>
                  </HbrSpinner>
                </div>
              ) : (
                <>
                  <HbrInput
                    value={searchTarget}
                    placeholder={`Search ${targetMode}`}
                    onHbr-input={(e: any) =>
                      setSearchTarget(String(e?.target?.value ?? e?.detail?.value ?? ''))
                    }
                  >
                    <HbrIcon slot="prefix" asset={magnifyingGlass} />
                  </HbrInput>

                  <div className="hbr-css__layout-row-sm hbr-css__layout-wrap">
                    <HbrButton variant="outline" onClick={selectAllFiltered}>
                      Select all shown
                    </HbrButton>
                    <HbrButton variant="outline" onClick={clearNewSelection}>
                      Clear new selections
                    </HbrButton>
                  </div>

                  <div
                    className="hbr-css__layout-col-sm"
                    style={{
                      maxHeight: '420px',
                      overflowY: 'auto',
                      paddingRight: '4px',
                    }}
                  >
                    {targetMode === 'users'
                      ? filteredUsers.map((u) => {
                        const id = String(u.id);
                        const row = userSelection[id] || {
                          id,
                          selected: false,
                          originalSelected: false,
                          priority: null,
                          dueDate: '',
                          markedForRemoval: false,
                        };

                        return (
                          <HbrCard
                            key={id}
                            selectable={!row.originalSelected}
                            selected={row.selected && !row.markedForRemoval}
                            role="button"
                            sentiment={
                              row.markedForRemoval
                                ? 'danger'
                                : row.originalSelected
                                  ? 'info'
                                  : row.selected
                                    ? 'success'
                                    : undefined
                            }
                            onClick={() => {
                              if (!row.originalSelected && !row.locked) toggleUser(id);
                            }}
                            style={
                              {
                                '--box-shadow': 'none',
                                '--border-width': '2px',
                              } as React.CSSProperties
                            }
                          >
                            <div className="hbr-css__layout-col-sm">
                              <div className="hbr-css__layout-row-sm hbr-css__layout-align-start">
                                <HbrIcon asset={user} style={{ fontSize: '20px' }} />
                                <div className="hbr-css__layout-col-3xs" style={{ flex: 1, minWidth: 0 }}>
                                  <div className="hbr-css__layout-row-sm hbr-css__layout-wrap hbr-css__layout-align-center">
                                    <div className="hbr-css__text-primary-sm-semibold">
                                      {getUserLabel(u)}
                                    </div>
                                    {getRowStateLabel(row) ? (
                                      <HbrTag size="small">{getRowStateLabel(row)}</HbrTag>
                                    ) : null}
                                    {row.status ? <HbrTag size="small">{row.status}</HbrTag> : null}
                                    {row.locked ? (
                                      <HbrTooltip content={LOCKED_TOOLTIP}>
                                        <HbrTag size="small">
                                          <span className="hbr-css__layout-row-3xs hbr-css__layout-align-center">
                                            <HbrIcon asset={lock} />
                                            Locked
                                          </span>
                                        </HbrTag>
                                      </HbrTooltip>
                                    ) : null}
                                  </div>
                                  <div className="hbr-css__text-secondary-xs">
                                    {u?.email || ''}
                                  </div>
                                  {u?.title ? (
                                    <div className="hbr-css__text-secondary-xs">{u.title}</div>
                                  ) : null}
                                </div>

                                {row.originalSelected && !row.locked ? (
                                  row.markedForRemoval ? (
                                    <HbrButton
                                      variant="outline"
                                      onClick={(e: any) => {
                                        e.stopPropagation();
                                        undoRemoveUser(id);
                                      }}
                                    >
                                      Undo Remove
                                    </HbrButton>
                                  ) : (
                                    <HbrButton
                                      variant="outline"
                                      onClick={(e: any) => {
                                        e.stopPropagation();
                                        markUserForRemoval(id);
                                      }}
                                    >
                                      Remove
                                    </HbrButton>
                                  )
                                ) : null}
                              </div>

                              <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
                                {row.originalSelected ? (
                                  <>
                                    <HbrTag size="small">{requiredLabel(row.isRequired)}</HbrTag>
                                    <HbrTag size="small">
                                      Current Priority: {getPriorityLabel(row.priority)}
                                    </HbrTag>
                                    {row.dueDate ? (
                                      <HbrTag size="small">Current Due: {row.dueDate}</HbrTag>
                                    ) : (
                                      <HbrTag size="small">No current due date</HbrTag>
                                    )}
                                  </>
                                ) : (
                                  <HbrTag size="small">Not currently assigned</HbrTag>
                                )}
                              </div>
                            </div>
                          </HbrCard>
                        );
                      })
                      : filteredGroups.map((g) => {
                        const id = g.key;
                        const row = groupSelection[id] || {
                          id,
                          selected: false,
                          originalSelected: false,
                          priority: null,
                          dueDate: '',
                          markedForRemoval: false,
                        };

                        return (
                          <HbrCard
                            key={id}
                            selectable={!row.originalSelected}
                            selected={row.selected && !row.markedForRemoval}
                            role="button"
                            sentiment={
                              row.markedForRemoval
                                ? 'danger'
                                : row.originalSelected
                                  ? 'info'
                                  : row.selected
                                    ? 'success'
                                    : undefined
                            }
                            onClick={() => {
                              if (!row.originalSelected) toggleGroup(id);
                            }}
                            style={
                              {
                                '--box-shadow': 'none',
                                '--border-width': '2px',
                              } as React.CSSProperties
                            }
                          >
                            <div className="hbr-css__layout-col-sm">
                              <div className="hbr-css__layout-row-sm hbr-css__layout-align-start">
                                <HbrIcon asset={usersIcon} style={{ fontSize: '20px' }} />
                                <div className="hbr-css__layout-col-3xs" style={{ flex: 1, minWidth: 0 }}>
                                  <div className="hbr-css__layout-row-sm hbr-css__layout-wrap hbr-css__layout-align-center">
                                    <div className="hbr-css__text-primary-sm-semibold">
                                      {getGroupLabel(g)}
                                    </div>
                                    <HbrTag size="small">{GROUP_TYPE_LABEL[g.groupType]}</HbrTag>
                                    {getRowStateLabel(row) ? (
                                      <HbrTag size="small">{getRowStateLabel(row)}</HbrTag>
                                    ) : null}
                                    {row.status ? <HbrTag size="small">{row.status}</HbrTag> : null}
                                  </div>
                                  <div className="hbr-css__text-secondary-xs">
                                    {g?.description || ''}
                                  </div>
                                </div>

                                {row.originalSelected ? (
                                  row.markedForRemoval ? (
                                    <HbrButton
                                      variant="outline"
                                      onClick={(e: any) => {
                                        e.stopPropagation();
                                        undoRemoveGroup(id);
                                      }}
                                    >
                                      Undo Remove
                                    </HbrButton>
                                  ) : (
                                    <HbrButton
                                      variant="outline"
                                      onClick={(e: any) => {
                                        e.stopPropagation();
                                        markGroupForRemoval(id);
                                      }}
                                    >
                                      Remove
                                    </HbrButton>
                                  )
                                ) : null}
                              </div>

                              <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
                                {row.originalSelected ? (
                                  <>
                                    <HbrTag size="small">{requiredLabel(row.isRequired)}</HbrTag>
                                    <HbrTag size="small">
                                      Current Priority: {getPriorityLabel(row.priority)}
                                    </HbrTag>
                                    {row.dueDate ? (
                                      <HbrTag size="small">Current Due: {row.dueDate}</HbrTag>
                                    ) : (
                                      <HbrTag size="small">No current due date</HbrTag>
                                    )}
                                  </>
                                ) : (
                                  <HbrTag size="small">Not currently assigned</HbrTag>
                                )}
                              </div>
                            </div>
                          </HbrCard>
                        );
                      })}
                  </div>

                  <div className="hbr-css__layout-row-sm hbr-css__layout-justify-between hbr-css__layout-align-center">
                    <div className="hbr-css__layout-row-sm hbr-css__layout-wrap">
                      <HbrTag size="small">{selectedCount} selected</HbrTag>
                      <HbrTag size="small">{existingCount} existing</HbrTag>
                      <HbrTag size="small">{newCount} new</HbrTag>
                      <HbrTag size="small">{willRemoveCount} will remove</HbrTag>
                      <HbrTag size="small">Apply Priority: {getPriorityLabel(priority)}</HbrTag>
                      <HbrTag size="small">Apply: {requiredLabel(isRequired)}</HbrTag>
                      <HbrTag size="small">
                        {notifyEffective ? 'Webex notification on' : 'No Webex notification'}
                      </HbrTag>
                      {dueDate ? <HbrTag size="small">Apply Due: {dueDate}</HbrTag> : null}
                      {!isJourneyMapAdmin && isManager ? (
                        <HbrTag size="small">Manager scope: your whole team</HbrTag>
                      ) : null}
                      {isJourneyMapAdmin ? <HbrTag size="small">Admin scope</HbrTag> : null}
                    </div>

                    <HbrButton
                      onClick={() => saveMutation.mutate()}
                      disabled={!selectedJourneyId || saveMutation.isPending}
                    >
                      <HbrIcon slot="prefix" asset={checkCircle} />
                      Save Assignment Changes
                    </HbrButton>
                  </div>
                </>
              )}
            </div>
          </HbrCard>
        </div>
      </div>

      <StbModal
        open={resultModalOpen}
        role={resultModalRole}
        title={resultModalTitle}
        onClose={async () => setResultModalOpen(false)}
        onSubmit={async () => setResultModalOpen(false)}
        submitLabel="Close"
        hideCancel
      >
        {resultModalBody}
      </StbModal>
    </>
  );
};

export default JourneyMapAssignmentsManagerPage;